import React from 'react';
import { useLocation } from 'react-router-dom';
import { useActivePanel, useUIStore } from '../../stores/uiStore';
import { SpeciesPanel } from '../TreeSpecies/SpeciesPanel';
import { AssessmentPanel } from '../SiteAssessment/AssessmentPanel';
import { CommunityPanel } from '../Community/CommunityPanel';
import { LayerControls } from '../Map/LayerControls';

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const activePanel = useActivePanel();
  const { setActivePanel } = useUIStore();

  // Auto-set active panel based on current route
  React.useEffect(() => {
    switch (location.pathname) {
      case '/species':
        setActivePanel('species');
        break;
      case '/assess':
        setActivePanel('assessment');
        break;
      case '/community':
        setActivePanel('community');
        break;
      default:
        setActivePanel(null);
        break;
    }
  }, [location.pathname, setActivePanel]);

  const renderPanelContent = () => {
    switch (activePanel) {
      case 'species':
        return <SpeciesPanel />;
      case 'assessment':
        return <AssessmentPanel />;
      case 'community':
        return <CommunityPanel />;
      default:
        return (
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Map Controls</h2>
            <LayerControls />
          </div>
        );
    }
  };

  return (
    <div className="sidebar-panel w-80 h-full">
      <div className="h-full flex flex-col">
        {/* Panel Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {activePanel === 'species' && 'Tree Species'}
              {activePanel === 'assessment' && 'Site Assessment'}
              {activePanel === 'community' && 'Community'}
              {!activePanel && 'Map Tools'}
            </h2>
            
            {/* Panel Navigation Tabs */}
            {location.pathname === '/' && (
              <div className="flex space-x-1">
                <button
                  onClick={() => setActivePanel('species')}
                  className={`
                    px-2 py-1 text-xs font-medium rounded transition-colors duration-200
                    ${activePanel === 'species' 
                      ? 'bg-forest-100 text-forest-700' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  Species
                </button>
                <button
                  onClick={() => setActivePanel('assessment')}
                  className={`
                    px-2 py-1 text-xs font-medium rounded transition-colors duration-200
                    ${activePanel === 'assessment' 
                      ? 'bg-forest-100 text-forest-700' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  Assess
                </button>
                <button
                  onClick={() => setActivePanel('community')}
                  className={`
                    px-2 py-1 text-xs font-medium rounded transition-colors duration-200
                    ${activePanel === 'community' 
                      ? 'bg-forest-100 text-forest-700' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  Community
                </button>
                <button
                  onClick={() => setActivePanel(null)}
                  className={`
                    px-2 py-1 text-xs font-medium rounded transition-colors duration-200
                    ${!activePanel 
                      ? 'bg-forest-100 text-forest-700' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  Layers
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {renderPanelContent()}
        </div>
      </div>
    </div>
  );
};
